import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Handoff } from "./types";

interface HandoffState {
  onboarding?: Handoff;
}

export function readHandoff(state: unknown): Handoff | null {
  const handoff = (state as HandoffState | null | undefined)?.onboarding;
  return handoff && typeof handoff === "object" ? handoff : null;
}

/** Router state without the handoff; `null` when nothing else was there. */
export function stripHandoff(state: unknown): Record<string, unknown> | null {
  if (!state || typeof state !== "object") return null;
  const { onboarding: _omit, ...rest } = state as HandoffState;
  return Object.keys(rest).length > 0 ? rest : null;
}

/**
 * Latches `location.state.onboarding` on first render, then replaces the
 * history entry without it so a reload or back/forward cannot replay it.
 */
export function useHandoff(): Handoff | null {
  const location = useLocation();
  const navigate = useNavigate();
  const [latched] = useState(() => readHandoff(location.state));

  const present = readHandoff(location.state) !== null;
  useEffect(() => {
    if (!present) return;
    navigate(location.pathname + location.search + location.hash, {
      replace: true,
      state: stripHandoff(location.state),
    });
  }, [
    present,
    location.pathname,
    location.search,
    location.hash,
    location.state,
    navigate,
  ]);

  return latched;
}
